import { Link } from "react-router-dom";
import { FilePlus2 } from "lucide-react";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/api";
import { STATUS_LABELS } from "../lib/brand";
import { PageTitle, Panel, StatusBadge, Empty, btnAccent } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const Stat = ({ label, value, to, id }) => {
  const body = (
    <div className="rounded-lg border border-border bg-card p-5 transition-colors hover:border-pulse/50" data-testid={`stat-${id}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-4xl font-semibold text-ink" data-testid={`stat-${id}-value`}>{value ?? 0}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function Dashboard() {
  const { can, user } = useAuth();
  const { data: d, loading } = useFetch("/admin/dashboard");
  if (loading || !d) return <BrandLoader />;
  return (
    <div data-testid="admin-dashboard">
      <PageTitle title={`Dobrodošli, ${user.name.split(" ")[0]}`} sub="Sve vrijednosti dolaze direktno iz baze podataka."
        actions={can("articles.create") && <Link to="/admin/articles/new" className={btnAccent} data-testid="dashboard-new-article"><FilePlus2 className="h-4 w-4" /> Novi članak</Link>} />
      <div className="mb-6 flex flex-wrap gap-2" data-testid="dashboard-quick-actions">
        {[["/admin/articles/new", "Novi članak", "articles.create"], ["/admin/articles/new?tip=vodic", "Novi vodič", "articles.create"], ["/admin/articles?status=draft", "Pregled nacrta", "articles.create"], ["/admin/articles?status=in_review", "Članci za odobravanje", "articles.review"], ["/admin/kalendar", "Kalendar objava", "articles.create"], ["/admin/media", "Medijska biblioteka", "media.upload"]].filter(([, , p]) => can(p)).map(([to, l]) => (
          <Link key={to} to={to} className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:border-pulse" data-testid={`quick-${l}`}>{l}</Link>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat id="articles-total" label="Ukupno članaka" value={d.articles_total} to="/admin/articles" />
        <Stat id="published" label="Objavljeno" value={d.articles_by_status.published} to="/admin/articles?status=published" />
        <Stat id="in-review" label="Na pregledu" value={d.articles_by_status.in_review + d.articles_by_status.fact_check} to="/admin/workflow" />
        {"contact_new" in d ? <Stat id="contact-new" label="Novi kontakt upiti" value={d.contact_new} to="/admin/inquiries" /> : <Stat id="my-articles" label="Moji članci" value={d.my_articles} />}
        <Stat id="categories" label="Aktivne kategorije" value={d.categories} to="/admin/categories" />
        {"media_assets" in d && <Stat id="media" label="Medijski fajlovi" value={d.media_assets} />}
        {"users_active" in d && <Stat id="users" label="Aktivni korisnici" value={d.users_active} />}
        {"demo_articles" in d && <Stat id="demo" label="DEMO članci" value={d.demo_articles} />}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {[["Moji zadaci (dodijeljeno meni)", d.my_tasks, "updated_at"], ["Moji tekstovi za doradu", d.my_revisions, "updated_at"], ["Zakazane objave", d.upcoming_scheduled, "scheduled_at"]].map(([t, list, k]) => (
          <Panel key={t} title={t} testId={`widget-${k}-${t.length}`}>
            {list?.length ? <ul className="space-y-2 text-sm">{list.map((a) => <li key={a.id} className="flex justify-between gap-2"><Link to={`/admin/articles/${a.id}`} className="truncate hover:underline">{a.headline}</Link><span className="shrink-0 text-xs text-muted-foreground">{fmtDateTime(a[k])}</span></li>)}</ul> : <Empty>Nema stavki.</Empty>}
          </Panel>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Članci po statusu" testId="dashboard-status-breakdown">
          <ul className="space-y-2 text-sm">
            {Object.entries(d.articles_by_status).map(([s, n]) => (
              <li key={s} className="flex items-center justify-between"><span>{STATUS_LABELS[s]}</span><span className="font-semibold tabular-nums">{n}</span></li>
            ))}
          </ul>
        </Panel>
        <Panel title="Nedavno uređeno" className="lg:col-span-2" testId="dashboard-recent-articles">
          {d.recent_articles.length ? (
            <ul className="divide-y divide-border">
              {d.recent_articles.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 py-2.5">
                  <Link to={`/admin/articles/${a.id}`} className="min-w-0 truncate text-sm font-medium hover:underline">{a.headline}</Link>
                  <span className="flex shrink-0 items-center gap-2"><StatusBadge status={a.status} /><span className="hidden text-xs text-muted-foreground sm:inline">{fmtDateTime(a.updated_at)}</span></span>
                </li>
              ))}
            </ul>
          ) : <Empty>Još nema članaka.</Empty>}
        </Panel>
      </div>
      {d.recent_audit && (
        <Panel title="Posljednje aktivnosti" className="mt-6" testId="dashboard-recent-audit">
          <ul className="divide-y divide-border text-sm">
            {d.recent_audit.map((l) => (
              <li key={l.id} className="flex flex-wrap justify-between gap-2 py-2"><span><code className="text-xs">{l.action}</code> <span className="text-muted-foreground">· {l.actor_email || "javni posjetilac"}</span></span><span className="text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</span></li>
            ))}
          </ul>
        </Panel>
      )}
      <p className="mt-6 text-xs text-muted-foreground">Analitika je u modulu Analitika, distribucija u modulu Distribucija; oglašavanje dolazi u kasnijoj fazi. Ovdje se ne prikazuju procijenjene ili izmišljene brojke.</p>
    </div>
  );
}
